import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Check, X, UserX } from 'lucide-react';
import { fmtMoney, fmtTime, chipFor } from '../../lib/dashboard/helpers';

/**
 * "Today's chair" panel — the list of today's bookings with quick
 * status actions. Handles the Everyone / Just me scope toggle for
 * shop admins that are also barbers.
 */
export const TodayChair = ({
  todayList, loading, busyId, myBarberId, myPerms, isBarber,
  todayScope, setTodayScope, updateStatus,
}) => {
  const navigate = useNavigate();
  // Filter view based on the session-only scope toggle.
  // 'shop' = every barber (default); 'mine' = only this shop_admin's
  // own barber row.
  const scoped = (todayScope === 'mine' && myBarberId)
    ? todayList.filter((b) => b.barber?.id === myBarberId)
    : todayList;

  // Cap the initial render at PAGE_SIZE so a busy shop's dashboard
  // doesn't render 40+ appointment rows straight off the mount. Item
  // 44 — the tester flagged this as "unbounded growth" that pushes
  // KPIs below the fold. Show-more reveals another page; toggling
  // scope resets the window.
  const PAGE_SIZE = 10;
  const [visibleCount, setVisibleCount] = React.useState(PAGE_SIZE);
  React.useEffect(() => { setVisibleCount(PAGE_SIZE); }, [todayScope]);
  const visible = scoped.slice(0, visibleCount);
  const hasMore = scoped.length > visibleCount;

  return (
    <div className="panel" data-testid="todays-chair">
      <div className="ph">
        <h3>Today&apos;s chair</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginLeft: 'auto' }}>
          {myBarberId && (
            <div className="seg" data-testid="todays-chair-scope">
              <button type="button" className={todayScope === 'shop' ? 'on' : ''} onClick={() => setTodayScope('shop')} data-testid="todays-chair-scope-shop">Everyone</button>
              <button type="button" className={todayScope === 'mine' ? 'on' : ''} onClick={() => setTodayScope('mine')} data-testid="todays-chair-scope-mine">Just me</button>
            </div>
          )}
          <Link to="/diary" className="link">Open diary →</Link>
        </div>
      </div>
      {loading ? (
        <div style={{ padding: 16 }}>
          {[1, 2, 3].map((i) => <div key={i} className="skel-line" style={{ width: `${60 + i * 10}%` }} />)}
        </div>
      ) : scoped.length === 0 ? (
        <div style={{ padding: '32px 16px', textAlign: 'center', color: 'var(--mute)', fontSize: 13 }}>
          {todayScope === 'mine' ? 'No bookings assigned to you today.' : 'No bookings today.'}
        </div>
      ) : (
        <>
          {visible.map((b) => {
          const cls = b.channel === 'manual' ? 'g' : '';
          const closed = ['completed', 'no_show', 'cancelled'].includes(b.status);
          const statusLabel = b.status === 'completed' ? 'Completed'
            : b.status === 'no_show' ? 'No-show'
            : b.status === 'cancelled' ? 'Cancelled' : null;
          const showActions = !isBarber || myPerms.canManageBookings === true;
          const mobileActions = closed ? (
            <span className={`pill status-pill s-${b.status}`}>{statusLabel}</span>
          ) : showActions ? (
            <div className="appt-actions" onClick={(e) => e.stopPropagation()}>
              <button type="button" className="mini-btn ok" disabled={busyId === b.id} onClick={() => updateStatus(b.id, 'completed')} title="Mark completed"><Check size={14} /></button>
              <button type="button" className="mini-btn warn" disabled={busyId === b.id} onClick={() => updateStatus(b.id, 'no_show')} title="Mark no-show"><UserX size={14} /></button>
              <button type="button" className="mini-btn danger" disabled={busyId === b.id} onClick={() => updateStatus(b.id, 'cancelled')} title="Cancel"><X size={14} /></button>
            </div>
          ) : null;
          const chip = chipFor(b);
          return (
            <div
              key={b.id}
              className={`appt ${cls} ${closed ? `closed s-${b.status}` : ''}`}
              data-testid={`appt-${b.id}`}
              onClick={() => navigate(`/diary?date=${b.starts_at.slice(0, 10)}&booking=${b.id}`)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/diary?date=${b.starts_at.slice(0, 10)}&booking=${b.id}`); }}
              style={{ cursor: 'pointer' }}
            >
              <span className="time">{fmtTime(b.starts_at)}</span>
              <div className="who">
                <b>{b.customer?.name || '—'}</b>
                <small>{b.service?.name} · <span className="mono">{b.service?.duration_min}</span> min</small>
              </div>
              <div className="barb">
                <span className="d">{(b.barber?.name || '?').charAt(0)}</span>
                {b.barber?.name?.split(' ')[0]}
              </div>
              <span className="amt">{fmtMoney(b.service?.price)}</span>
              <span className={`pay-chip ${chip.cls}`} data-testid={`appt-pay-chip-${b.id}`} data-chip-state={chip.cls}>
                {chip.label}
              </span>
              {closed ? (
                <span className={`pill status-pill s-${b.status}`} data-testid={`appt-status-${b.id}`}>
                  {statusLabel}
                </span>
              ) : showActions ? (
                <div className="appt-actions" onClick={(e) => e.stopPropagation()}>
                  <button type="button" className="mini-btn ok" disabled={busyId === b.id} onClick={() => updateStatus(b.id, 'completed')} title="Mark completed" data-testid={`appt-complete-${b.id}`}><Check size={14} /></button>
                  <button type="button" className="mini-btn warn" disabled={busyId === b.id} onClick={() => updateStatus(b.id, 'no_show')} title="Mark no-show" data-testid={`appt-noshow-${b.id}`}><UserX size={14} /></button>
                  <button type="button" className="mini-btn danger" disabled={busyId === b.id} onClick={() => updateStatus(b.id, 'cancelled')} title="Cancel" data-testid={`appt-cancel-${b.id}`}><X size={14} /></button>
                </div>
              ) : null}
              <div className="appt-mobile-tail">
                <small className="appt-svc-m">{b.service?.name} · <span className="mono">{b.service?.duration_min}</span> min</small>
                {mobileActions}
              </div>
            </div>
          );
        })}
          {hasMore && (
            <div style={{ padding: 12, textAlign: 'center', borderTop: '1px solid var(--line)' }}>
              <span style={{ fontSize: 12, color: 'var(--mute)', marginRight: 12 }}>
                Showing {visible.length} of {scoped.length}
              </span>
              <button
                type="button"
                className="btn s"
                onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                data-testid="todays-chair-show-more"
              >
                Show more
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};
